/**
 * CountConditionEditor Component
 * 
 * This component provides a specialized interface for editing count-based conditions
 * in Azure Policies. It allows users to:
 * 1. Count resources matching specific criteria
 * 2. Apply numeric comparisons (greater than, less than, equals)
 * 3. Create nested conditions for filtering counted resources
 * 4. Toggle NOT operators at multiple levels
 * 
 * Features:
 * - Searchable field selection for counting
 * - Nested condition groups for filtering
 * - Support for NOT operations at count and where levels
 * - Infinite scrolling for large field lists
 * 
 * @example
 * ```tsx
 * <CountConditionEditor
 *   condition={{
 *     count: { field: "virtualMachines", where: { anyOf: [] } },
 *     greater: 5
 *   }}
 *   onUpdate={(condition) => handleUpdate(condition)}
 * />
 * ```
 */

import React from 'react';
import { CountCondition } from '../../types/policy';
import { CountFieldSelect } from './components/CountFieldSelect';
import { CountOperatorSelect } from './components/CountOperatorSelect';
import { useCountCondition } from './hooks/useCountCondition';

/**
 * Props for the CountConditionEditor component
 * @property condition - The count condition being edited
 * @property onUpdate - Callback for condition updates
 * @property onRemove - Optional callback for removing the condition
 */
interface CountConditionEditorProps {
  condition: CountCondition;
  onUpdate: (condition: CountCondition) => void;
  onRemove?: () => void;
}

/**
 * CountConditionEditor Component
 * 
 * Provides a complete interface for editing count-based conditions, including:
 * - Field selection for specifying what to count
 * - Operator selection for comparison (greater, less, equals)
 * - Numeric value input
 * - Where clause for filtering counted items
 * - NOT operation support at both count and where levels
 */
export const CountConditionEditor: React.FC<CountConditionEditorProps> = ({
  condition,
  onUpdate,
  onRemove
}) => {
  // Initialize count condition state and handlers
  const {
    currentOperator,     // Current comparison operator (greater, less, equals)
    currentValue,        // Current numeric value for comparison
    countField,          // Field being counted
    isValueCount,
    handleFieldChange,   // Handler for field changes
    handleOperatorChange,// Handler for operator changes
    handleValueChange,    // Handler for value changes
    handleValueCountToggle
  } = useCountCondition(condition, onUpdate);

  return (
    <div className="flex flex-col space-y-4 w-full bg-gray-50 dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Field selection for counting */}
      <div className="w-full border-b border-gray-200 dark:border-gray-700 pb-2">
        <div className="flex justify-between items-center mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {isValueCount ? 'Value' : 'Field'}
          </label>
          <div className="flex items-center">
            <label htmlFor="value-toggle" className="mr-2 text-sm text-gray-600 dark:text-gray-400">Use Value</label>
            <input
              type="checkbox"
              id="value-toggle"
              checked={isValueCount}
              onChange={handleValueCountToggle}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
          </div>
        </div>
        <CountFieldSelect 
          value={countField ?? ''} 
          onChange={handleFieldChange}
          placeholder={isValueCount ? "e.g. [field('Microsoft.Web/sites/publicNetworkAccess')]" : "Search fields or enter custom value..."}
        />
      </div>
      {/* Operator selection */}
      <div className="w-full border-b border-gray-200 dark:border-gray-700 pb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operator</label>
        <CountOperatorSelect value={currentOperator} onChange={handleOperatorChange} />
      </div>
      {/* Numeric value input as textarea */}
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Value</label>
        <textarea
          className="block w-full min-h-[80px] resize-y rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-2 text-sm text-gray-800 dark:text-gray-200 focus:border-blue-500 focus:ring-blue-500"
          value={currentValue}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder="Enter value"
        />
      </div>
    </div>
  );
};

export default CountConditionEditor;
